import { writable, derived } from 'svelte/store';
import type { HistoryEntry, Plan, PlanSession, PomodoroConfig } from '$lib/domain/types';
import { RECOVERY_GAP_MS, type TimerMetrics } from '$lib/domain/timer';
import { planRepo, planSessionRepo } from '$lib/db/repo';
import { createTombstonePayload } from '$lib/db/sync';
import { syncEngine } from './sync';
import { buildPlanSessions } from '$lib/domain/scheduler';
import { historyStore } from './history';
import { moveById } from '$lib/domain/blueprint';
import { completeSessionState, type SessionCompletionResult } from '$lib/domain/sessionTransition';

const defaultConfig: PomodoroConfig = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  sessionsBeforeLongBreak: 4,
  totalSessions: 6
};

interface PlanState {
  plans: Plan[];
  activePlanId: string | null;
  activeSessionIndex: number;
  sessions: PlanSession[];
  timerBusy: boolean;
  loaded: boolean;
}

interface SessionReviewInput {
  rating?: number;
  mood?: HistoryEntry['mood'];
  notes?: string;
}

interface PlanDraftInput {
  title: string;
  startAt: string;
  autoStart: boolean;
  config: PomodoroConfig;
}

const ACTIVE_PLAN_KEY = 'ff_active_plan_id';
const ACTIVE_SESSION_KEY = 'ff_active_session_index';

const readRuntimeState = () => {
  if (typeof localStorage === 'undefined') return { activePlanId: null, activeSessionIndex: 0 };
  return {
    activePlanId: localStorage.getItem(ACTIVE_PLAN_KEY),
    activeSessionIndex: Number(localStorage.getItem(ACTIVE_SESSION_KEY) ?? 0)
  };
};

const persistRuntimeState = (planId: string | null, sessionIndex = 0) => {
  if (typeof localStorage === 'undefined') return;
  if (planId) {
    localStorage.setItem(ACTIVE_PLAN_KEY, planId);
    localStorage.setItem(ACTIVE_SESSION_KEY, String(sessionIndex));
  } else {
    localStorage.removeItem(ACTIVE_PLAN_KEY);
    localStorage.removeItem(ACTIVE_SESSION_KEY);
  }
};

const sameConfig = (left: PomodoroConfig, right: PomodoroConfig) =>
  left.workDuration === right.workDuration &&
  left.shortBreakDuration === right.shortBreakDuration &&
  left.longBreakDuration === right.longBreakDuration &&
  left.sessionsBeforeLongBreak === right.sessionsBeforeLongBreak &&
  left.totalSessions === right.totalSessions;

const timestampOrder = (value: string) => {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
};

const planDisplayOrder = (plan: Plan) =>
  typeof plan.sortOrder === 'number' && Number.isFinite(plan.sortOrder) ? plan.sortOrder : timestampOrder(plan.startAt);

const sortPlansByStart = (plans: Plan[]) =>
  [...plans].sort(
    (a, b) =>
      timestampOrder(a.startAt) - timestampOrder(b.startAt) ||
      planDisplayOrder(a) - planDisplayOrder(b) ||
      timestampOrder(a.createdAt) - timestampOrder(b.createdAt) ||
      a.id.localeCompare(b.id)
  );

const sortPlansForDisplay = (plans: Plan[]) =>
  [...plans].sort((a, b) => planDisplayOrder(a) - planDisplayOrder(b) || timestampOrder(a.startAt) - timestampOrder(b.startAt));

const createPlansStore = () => {
  const { subscribe, set, update } = writable<PlanState>({
    plans: [],
    activePlanId: null,
    activeSessionIndex: 0,
    sessions: [],
    timerBusy: false,
    loaded: false
  });

  let snapshot: PlanState = { plans: [], activePlanId: null, activeSessionIndex: 0, sessions: [], timerBusy: false, loaded: false };
  let activationInFlight = false;
  subscribe((value) => {
    snapshot = value;
  });

  const loadLocalPlans = async () => {
    const plans = await planRepo.list();
    const sorted = sortPlansForDisplay(plans);
    const runtime = readRuntimeState();
    // A remote or legacy `active` status does not prove this browser started the timer.
    const activePlan = sorted.find((plan) => plan.id === runtime.activePlanId && plan.status === 'active') ?? null;

    if (!activePlan) {
      persistRuntimeState(null);
      set({ plans: sorted, activePlanId: null, activeSessionIndex: 0, sessions: [], timerBusy: snapshot.timerBusy, loaded: snapshot.loaded });
      return;
    }

    const sessions = await planSessionRepo.list(activePlan.id);
    const firstIncompleteIndex = sessions.findIndex((session) => !session.completed);
    if (sessions.length > 0 && firstIncompleteIndex === -1) {
      const completedPlan: Plan = { ...activePlan, status: 'completed', updatedAt: new Date().toISOString() };
      await planRepo.upsert(completedPlan);
      await syncEngine.enqueueChange('plans', completedPlan.id, completedPlan);
      persistRuntimeState(null);
      set({
        plans: sortPlansForDisplay(replacePlan(sorted, completedPlan)),
        activePlanId: null,
        activeSessionIndex: 0,
        sessions: [],
        timerBusy: snapshot.timerBusy,
        loaded: snapshot.loaded
      });
      return;
    }

    const restoredIndex =
      Number.isInteger(runtime.activeSessionIndex) &&
      runtime.activeSessionIndex >= 0 &&
      runtime.activeSessionIndex < sessions.length &&
      !sessions[runtime.activeSessionIndex]?.completed
        ? runtime.activeSessionIndex
        : Math.max(firstIncompleteIndex, 0);

    persistRuntimeState(activePlan.id, restoredIndex);
    set({ plans: sorted, activePlanId: activePlan.id, activeSessionIndex: restoredIndex, sessions, timerBusy: snapshot.timerBusy, loaded: snapshot.loaded });
  };

  const load = async () => {
    try {
      await loadLocalPlans();
    } finally {
      // Hydration has settled even when storage fails; the shell reports that error separately.
      update((state) => ({ ...state, loaded: true }));
    }
  };

  const refreshFromSync = async () => {
    const plans = await planRepo.list();
    update((state) => {
      // Cloud records refresh the planner, never the current browser's timer.
      const localActivePlan = state.plans.find((plan) => plan.id === state.activePlanId);
      const visiblePlans = localActivePlan
        ? [...plans.filter((plan) => plan.id !== localActivePlan.id), localActivePlan]
        : plans;
      return { ...state, plans: sortPlansForDisplay(visiblePlans) };
    });
  };

  const schedule = async (title: string, startAt: string, autoStart: boolean, config = defaultConfig) => {
    const now = new Date().toISOString();
    const plan: Plan = {
      id: crypto.randomUUID(),
      title,
      startAt,
      autoStart,
      status: 'scheduled',
      configSnapshot: config,
      createdAt: now,
      updatedAt: now
    };
    const sessions = buildPlanSessions(plan.id, config);
    await planRepo.upsert(plan);
    await planSessionRepo.bulkUpsert(sessions);
    await syncEngine.enqueueChange('plans', plan.id, plan);
    await syncEngine.enqueueChange('plan_sessions', plan.id, sessions);

    update((state) => ({
      ...state,
      plans: sortPlansForDisplay([plan, ...state.plans])
    }));
  };

  const updateScheduledPlan = async (planId: string, input: PlanDraftInput) => {
    const current = snapshot.plans.find((plan) => plan.id === planId);
    if (!current || current.status !== 'scheduled') return;

    const configChanged = !sameConfig(current.configSnapshot, input.config);
    const updated: Plan = {
      ...current,
      title: input.title.trim() || 'Focus plan',
      startAt: input.startAt,
      autoStart: input.autoStart,
      configSnapshot: input.config,
      updatedAt: new Date().toISOString()
    };

    await planRepo.upsert(updated);
    await syncEngine.enqueueChange('plans', updated.id, updated);

    if (configChanged) {
      const nextSessions = buildPlanSessions(updated.id, input.config);
      const removedSessions = await planSessionRepo.replaceForPlan(updated.id, nextSessions);
      for (const session of removedSessions) {
        await syncEngine.enqueueChange('plan_sessions', session.id, createTombstonePayload(session.id));
      }
      await syncEngine.enqueueChange('plan_sessions', updated.id, nextSessions);
    }

    update((state) => ({
      ...state,
      plans: sortPlansForDisplay(replacePlan(state.plans, updated))
    }));
  };

  const setTimerBusy = (busy: boolean) => {
    if (snapshot.timerBusy === busy) return;
    update((state) => ({ ...state, timerBusy: busy }));
  };

  const startPlan = async (planId: string): Promise<boolean> => {
    if (snapshot.activePlanId === planId) return true;
    if (activationInFlight || snapshot.activePlanId || snapshot.timerBusy) return false;
    const plan = snapshot.plans.find((item) => item.id === planId);
    if (!plan || plan.status !== 'scheduled') return false;

    activationInFlight = true;
    try {
      const sessions = await planSessionRepo.list(planId);
      const activeSessionIndex = sessions.findIndex((session) => !session.completed);
      const current = snapshot.plans.find((item) => item.id === planId);
      if (activeSessionIndex < 0 || snapshot.activePlanId || snapshot.timerBusy || current?.status !== 'scheduled') {
        return false;
      }

      const updated: Plan = { ...current, status: 'active', updatedAt: new Date().toISOString() };
      await planRepo.upsert(updated);
      persistRuntimeState(planId, activeSessionIndex);
      update((state) => ({
        ...state,
        activePlanId: planId,
        activeSessionIndex,
        sessions,
        plans: sortPlansForDisplay(replacePlan(state.plans, updated))
      }));
      await syncEngine.enqueueChange('plans', updated.id, updated);
      return true;
    } finally {
      activationInFlight = false;
    }
  };

  const startNextScheduled = async () => {
    const next = getNextScheduled();
    if (next && (await startPlan(next.id))) {
      return next;
    }
    return null;
  };

  const skipPlan = async (planId: string) => {
    const plan = snapshot.plans.find((item) => item.id === planId);
    if (!plan || plan.status === 'completed' || plan.status === 'skipped') return;
    const updated: Plan = { ...plan, status: 'skipped', updatedAt: new Date().toISOString() };
    await planRepo.upsert(updated);
    await syncEngine.enqueueChange('plans', updated.id, updated);

    const wasActive = snapshot.activePlanId === planId;
    if (wasActive) persistRuntimeState(null);
    update((state) => ({
      ...state,
      activePlanId: wasActive ? null : state.activePlanId,
      activeSessionIndex: wasActive ? 0 : state.activeSessionIndex,
      sessions: wasActive ? [] : state.sessions,
      plans: sortPlansForDisplay(replacePlan(state.plans, updated))
    }));
  };

  const markSessionStarted = async () => {
    const session = snapshot.sessions[snapshot.activeSessionIndex];
    if (!session || session.startedAt) return;
    const updatedSession: PlanSession = { ...session, startedAt: new Date().toISOString() };
    await planSessionRepo.bulkUpsert([updatedSession]);
    await syncEngine.enqueueChange('plan_sessions', updatedSession.id, updatedSession);
    update((state) => ({
      ...state,
      sessions: replaceSession(state.sessions, updatedSession)
    }));
  };

  const completeSession = async (
    review: SessionReviewInput = {},
    metrics: TimerMetrics = { interruptions: 0, pausedSeconds: 0 },
    taskId?: string,
    expectedSessionId?: string | null,
    completedAtMs = Date.now()
  ): Promise<SessionCompletionResult | null> => {
    const completion = completeSessionState(
      snapshot.sessions,
      snapshot.activeSessionIndex,
      new Date(completedAtMs).toISOString(),
      expectedSessionId
    );
    if (!completion) return null;
    const updatedSession = completion.completedSession;
    await planSessionRepo.bulkUpsert([updatedSession]);
    await syncEngine.enqueueChange('plan_sessions', updatedSession.id, updatedSession);
    const historyEntry = {
      id: crypto.randomUUID(),
      sessionId: updatedSession.id,
      planId: updatedSession.planId,
      // History duration is credited whole minutes. Missing observations never
      // become work simply because the wall-clock deadline passed during sleep.
      duration: metrics.focusedSeconds == null ? updatedSession.duration :
        Math.floor(Math.min(updatedSession.duration * 60, Math.max(0, metrics.focusedSeconds)) / 60),
      sessionType: updatedSession.type,
      taskId,
      rating: review.rating,
      mood: review.mood,
      notes: review.notes?.trim() || undefined,
      interruptions: metrics.interruptions,
      pausedSeconds: metrics.pausedSeconds,
      createdAt: new Date().toISOString()
    };
    await historyStore.add(historyEntry);
    await syncEngine.enqueueChange('history', historyEntry.id, historyEntry);

    const nextIndex = snapshot.activeSessionIndex + 1;
    if (completion.planCompleted) {
      await finishPlan(snapshot.activePlanId);
    } else {
      persistRuntimeState(snapshot.activePlanId, nextIndex);
      update((state) => ({
        ...state,
        activeSessionIndex: nextIndex,
        sessions: replaceSession(state.sessions, updatedSession)
      }));
    }
    return completion;
  };

  const finishPlan = async (planId: string | null) => {
    if (!planId) return;
    const plan = snapshot.plans.find((item) => item.id === planId);
    if (!plan) return;
    const updated: Plan = { ...plan, status: 'completed', updatedAt: new Date().toISOString() };
    await planRepo.upsert(updated);
    await syncEngine.enqueueChange('plans', updated.id, updated);
    persistRuntimeState(null);
    update((state) => ({
      ...state,
      activePlanId: null,
      activeSessionIndex: 0,
      sessions: [],
      plans: sortPlansForDisplay(replacePlan(state.plans, updated))
    }));
  };

  const checkForDuePlans = async (): Promise<Plan | null> => {
    if (activationInFlight || snapshot.activePlanId || snapshot.timerBusy) return null;
    const now = Date.now();
    const due = getNextScheduled((plan) => Date.parse(plan.startAt) <= now);
    if (!due) return null;
    if (due.autoStart && now - Date.parse(due.startAt) < RECOVERY_GAP_MS) {
      return (await startPlan(due.id)) ? due : null;
    }
    return due;
  };

  const getNextScheduled = (filter?: (plan: Plan) => boolean) => {
    const scheduled = snapshot.plans.filter((plan) => plan.status === 'scheduled');
    const sorted = sortPlansByStart(scheduled);
    if (filter) return sorted.find(filter) ?? null;
    return sorted[0] ?? null;
  };

  const moveScheduledPlan = async (activeId: string, overId: string) => {
    const scheduled = sortPlansForDisplay(snapshot.plans.filter((plan) => plan.status === 'scheduled'));
    const reordered = moveById(scheduled, activeId, overId);
    if (reordered === scheduled) return;

    const now = new Date().toISOString();
    const updatedScheduled = reordered.map((plan, index) => ({ ...plan, sortOrder: index, updatedAt: now }));
    await Promise.all(
      updatedScheduled.map(async (plan) => {
        await planRepo.upsert(plan);
        await syncEngine.enqueueChange('plans', plan.id, plan);
      })
    );
    const replacement = new Map(updatedScheduled.map((plan) => [plan.id, plan]));
    update((state) => ({
      ...state,
      plans: sortPlansForDisplay(state.plans.map((plan) => replacement.get(plan.id) ?? plan))
    }));
  };

  const replacePlan = (plans: Plan[], updated: Plan) => plans.map((plan) => (plan.id === updated.id ? updated : plan));

  const replaceSession = (sessions: PlanSession[], updated: PlanSession) =>
    sessions.map((session) => (session.id === updated.id ? updated : session));

  return {
    subscribe,
    load,
    refreshFromSync,
    schedule,
    updateScheduledPlan,
    setTimerBusy,
    startPlan,
    startNextScheduled,
    skipPlan,
    moveScheduledPlan,
    markSessionStarted,
    completeSession,
    checkForDuePlans
  };
};

export const plansStore = createPlansStore();

export const activePlan = derived(plansStore, ($plans) => {
  return $plans.activePlanId ? $plans.plans.find((plan) => plan.id === $plans.activePlanId) ?? null : null;
});

export const activeSession = derived(plansStore, ($plans) => {
  return $plans.activePlanId ? $plans.sessions[$plans.activeSessionIndex] ?? null : null;
});

export const nextScheduledPlan = derived(plansStore, ($plans) => {
  const scheduled = $plans.plans.filter((plan) => plan.status === 'scheduled');
  return sortPlansByStart(scheduled)[0] ?? null;
});
